import { castShapeOf, CastShape, ELEMENTS, ELEMENT_META } from '../config/settings.js';
import { ELEMENT_SIGILS } from './glyphs.js';

const FIXED_QUICK_ABILITIES = Object.freeze(ELEMENTS.slice(0, 6));
const DEFAULT_BOOK_ABILITY = ELEMENTS[6];

function abilityCard(element, key = ELEMENT_META[element].key, extraClass = '') {
  const meta = ELEMENT_META[element];
  return `
    <button class="ability-card ${extraClass}" type="button" data-element="${element}"
      style="--accent:${meta.accent}" aria-label="${meta.label}, shortcut ${key}">
      <span class="ability-card__sweep" data-sweep></span>
      <span class="ability-card__key">${key}</span>
      <span class="ability-card__glyph">${ELEMENT_SIGILS[element] ?? ''}</span>
      <span class="ability-card__label">${meta.label}</span>
      <span class="ability-card__state" data-reactivation-state></span>
    </button>`;
}

const SPELLBOOK_DESCRIPTIONS = Object.freeze({
  ice: 'A fracture front races out and tears crystal behind it.',
  thunder: 'Lightning filaments snap out, gutter, and fade.',
  meteor: 'A burning rock drops from above and breaks the ground.',
  beam: 'A focused ray holds a bright line between the hands.',
  snare: 'A current trap opens on the circle and holds the target.',
  glacier: 'A ring of ice blades closes around the footprint.',
  ray: 'A narrow arc discharge lashes forward in a single line.',
  backflow: 'A rift folds the cast back through a dark seam.',
  prism: 'A prism wake unfolds into a delayed triangular enclosure.',
  horizon: 'A dark well gathers matter before collapsing inward.',
  razor: 'A choir of wind blades plants, then cuts across the field.',
  tide: 'A rising vault suspends the zone before dropping it.',
  sunwheel: 'A rotating solar wheel sweeps marked sectors.',
  faultfold: 'A broken seam rises into a staggered earthen cover.',
  bramble: 'Orbiting thorn sentries wait inside the field.',
  cataclysm: 'A charged fireball blooms, then crashes down in an arc.',
  runic: 'A runic aegis opens as a layered glyph around the caster.',
  shadowstep: 'A split shadow moves the assassin and leaves an echo.',
  camouflage: 'Smoke and violet shadow hide the assassin until the reveal.'
});

function spellbookGroups(elements = ELEMENTS) {
  const groups = new Map();
  for (const element of elements) {
    const category = ELEMENT_META[element].category || 'Arcane';
    if (!groups.has(category)) groups.set(category, []);
    groups.get(category).push(element);
  }
  return [...groups];
}

function spellbookCategories() {
  return spellbookGroups().map(([category, elements]) => ({
    category,
    count: elements.length,
    accent: ELEMENT_META[elements[0]]?.accent ?? '#7fd6ff'
  }));
}

function spellbookDescription(element, meta) {
  return SPELLBOOK_DESCRIPTIONS[element] || meta.role || `${meta.category || 'Arcane'} spell`;
}

function spellbookShape(element) {
  return castShapeOf(element) === CastShape.ZONE ? 'Circle' : 'Arrow';
}

/**
 * Heads-up display: the ability bar, controls, live stats and toasts.
 *
 * Plain DOM — no framework. The bar is built from `ELEMENTS`, so a new ability
 * appears in it on its own; the slots are the only interactive part, and they
 * mirror the keyboard shortcuts through `onAbility`.
 *
 * The cooldown sweep is a `conic-gradient` driven by a CSS custom property, so
 * updating it every frame is one `setProperty` call and never touches layout.
 */
export class HUD {
  constructor(root) {
    this.root = root;
    this.onAbility = null;
    this.onAbilityRelease = null;
    this.onSpellbookSelect = null;
    this.quickAbility = DEFAULT_BOOK_ABILITY;
    this._spellbookReturnFocus = null;
    this._toastTimer = 0;
    this._statsAccumulator = 0;
    this._frames = 0;
    this._fps = 0;
    /** Last sweep ratio pushed to the DOM, per element. */
    this._cooldownShown = new Map();
    this._armedShown = null;
    this._spellbookFilter = 'all';

    root.innerHTML = `
      <div class="hud__panel hud__title">
        Elemental Sandbox
        <span data-blurb>Select a sigil, aim, then click to cast.</span>
      </div>

      <div class="hud__panel hud__stats">
        <div>FPS <b data-stat="fps">—</b></div>
        <div>Particles <b data-stat="particles">0</b></div>
        <div>Instances <b data-stat="spikes">0</b></div>
        <div>Draw calls <b data-stat="calls">0</b></div>
      </div>

      <div class="hud__panel hud__help">
        <div><strong>Q</strong> — Frost Lance &nbsp; <strong>E</strong> — Storm Lance</div>
        <div><strong>R</strong> — Cinder Fall &nbsp; <strong>F</strong> — Nova Beam</div>
        <div><strong>V</strong> — Voltaic Snare &nbsp; <strong>X</strong> — Glacial Crown</div>
        <div><strong>T</strong> — selected Spellbook magic</div>
        <div><strong>Spellbook</strong> — choose the T slot</div>
        <div class="hud__help-note">Zone spells use a circular ground target. Hold spells launch on release.</div>
        <div><strong>Move</strong> — aim &nbsp; <strong>Left click</strong> — cast</div>
        <div><strong>Esc / right click</strong> — cancel the cast</div>
        <div><strong>Right drag</strong> — orbit &nbsp; <strong>Scroll</strong> — zoom</div>
        <div style="margin-top:6px">
          <kbd>G</kbd> editor &nbsp; <kbd>P</kbd> pause &nbsp; <kbd>C</kbd> clear
        </div>
        <div><kbd>H</kbd> hide this</div>
        <div class="hud__help-note">Paused still applies every editor change.</div>
      </div>

      <div class="hud__abilities">
        ${FIXED_QUICK_ABILITIES.map((element) => abilityCard(element)).join('')}
        ${abilityCard(DEFAULT_BOOK_ABILITY, 'T', 'ability-card--quick')}
        <button class="spellbook-button" type="button" data-spellbook-open aria-haspopup="dialog">
          <svg class="spellbook-button__icon" viewBox="0 0 48 48" aria-hidden="true">
            <path d="M6 10.5c7.2-1.4 12.9.2 18 4.8v25.2c-5.1-4.6-10.8-6.2-18-4.8z"/>
            <path d="M42 10.5c-7.2-1.4-12.9.2-18 4.8v25.2c5.1-4.6 10.8-6.2 18-4.8z"/>
            <path d="M10 16.5c3.7-.2 6.8.6 9.8 2.6M38 16.5c-3.7-.2-6.8.6-9.8 2.6"/>
          </svg>
          <span class="spellbook-button__label">Spellbook</span>
          <span class="spellbook-button__hint">Choose T</span>
        </button>
      </div>

      <div class="spellbook" data-spellbook hidden>
        <section class="spellbook__panel" role="dialog" aria-modal="true" aria-labelledby="spellbook-title">
          <header class="spellbook__header">
            <div class="spellbook__brand">
              <span class="spellbook__eyebrow">Spellbook</span>
              <span class="spellbook__count"><b data-spellbook-count>${ELEMENTS.length}</b> of ${ELEMENTS.length}</span>
              <h2 id="spellbook-title">Choose a spell</h2>
            </div>
            <label class="spellbook__search">
              <span aria-hidden="true">⌕</span>
              <input type="search" data-spellbook-search placeholder="Type to filter — name, school, description" autocomplete="off">
            </label>
            <button class="spellbook__close" type="button" data-spellbook-close aria-label="Close Spellbook">×</button>
          </header>
          <nav class="spellbook__filters" data-spellbook-filters aria-label="Spell schools">
            <button class="spellbook__filter is-active" type="button" data-spellbook-filter="all"
              style="--school-accent:#7fd6ff" aria-pressed="true">
              <i></i><span>All</span><b>${ELEMENTS.length}</b>
            </button>
            ${spellbookCategories().map(({ category, count, accent }) => `
              <button class="spellbook__filter" type="button" data-spellbook-filter="${category}"
                style="--school-accent:${accent}" aria-pressed="false">
                <i></i><span>${category}</span><b>${count}</b>
              </button>`).join('')}
          </nav>
          <div class="spellbook__groups">
            ${spellbookGroups().map(([category, elements]) => `
              <section class="spellbook__group" data-spellbook-group="${category}"
                style="--school-accent:${ELEMENT_META[elements[0]]?.accent ?? '#7fd6ff'}">
                <h3><i></i><span>${category}</span><b>${elements.length}</b></h3>
                <div class="spellbook__grid">
                  ${elements.map((element) => {
                    const meta = ELEMENT_META[element];
                    const description = spellbookDescription(element, meta);
                    const shape = spellbookShape(element);
                    return `
                      <button class="spellbook-card" type="button" data-spellbook-element="${element}"
                        data-spellbook-category="${category}"
                        data-spellbook-search="${`${element} ${meta.label} ${category} ${description} ${shape}`.toLowerCase()}"
                        style="--accent:${meta.accent}" aria-label="Equip ${meta.label}" aria-pressed="false">
                        <span class="spellbook-card__top">
                          <span class="spellbook-card__sigil">${ELEMENT_SIGILS[element] ?? ''}</span>
                          <span class="spellbook-card__shape">${shape}</span>
                        </span>
                        <span class="spellbook-card__copy">
                          <strong>${meta.label}</strong>
                          <small>${description}</small>
                        </span>
                        <span class="spellbook-card__key">${meta.key}</span>
                        <span class="spellbook-card__equip">Bind to T</span>
                      </button>`;
                  }).join('')}
                </div>
              </section>`).join('')}
            <p class="spellbook__empty" data-spellbook-empty hidden>No spells match that search.</p>
          </div>
          <footer class="spellbook__footer">
            <span><kbd>↑</kbd><kbd>↓</kbd><kbd>←</kbd><kbd>→</kbd> browse</span>
            <span><kbd>PgUp</kbd> <kbd>PgDn</kbd> school</span>
            <span><kbd>Enter</kbd> cast</span>
            <span><kbd>Esc</kbd> close</span>
            <button type="button" class="spellbook__reset" data-spellbook-reset>Reset loadout</button>
          </footer>
        </section>
      </div>

      <div class="hud__toast" data-toast></div>
      <div class="hud__paused" data-paused>Paused</div>
    `;

    this.cards = new Map();
    for (const card of root.querySelectorAll('.ability-card')) {
      const elementCards = this.cards.get(card.dataset.element) || [];
      elementCards.push(card);
      this.cards.set(card.dataset.element, elementCards);
      card.addEventListener('pointerdown', (event) => {
        event.stopPropagation();
        // Synthetic pointer events used by the smoke test are not active
        // pointers and browsers reject capture for them.
        try { card.setPointerCapture?.(event.pointerId); } catch {}
        this.onAbility?.(card.dataset.element);
      });
      card.addEventListener('pointerup', (event) => {
        event.stopPropagation();
        this.onAbilityRelease?.(card.dataset.element);
      });
    }

    this.quickCard = root.querySelector('.ability-card--quick');
    this.spellbook = root.querySelector('[data-spellbook]');
    this.spellbookButton = root.querySelector('[data-spellbook-open]');
    this.spellbookSearch = root.querySelector('[data-spellbook-search]');
    this.spellbookCount = root.querySelector('[data-spellbook-count]');
    this.spellbookEmpty = root.querySelector('[data-spellbook-empty]');
    this.spellbookButton.addEventListener('pointerdown', (event) => event.stopPropagation());
    this.spellbookButton.addEventListener('click', () => this.openSpellbook());
    root.querySelector('[data-spellbook-close]').addEventListener('click', () => this.closeSpellbook());
    this.spellbookSearch.addEventListener('input', () => this._applySpellbookFilter());
    this.spellbook.addEventListener('pointerdown', (event) => event.stopPropagation());
    this.spellbook.addEventListener('click', (event) => {
      if (event.target === this.spellbook) {
        this.closeSpellbook();
        return;
      }
      const filter = event.target.closest('[data-spellbook-filter]');
      if (filter) {
        this.setSpellbookFilter(filter.dataset.spellbookFilter);
        return;
      }
      if (event.target.closest('[data-spellbook-reset]')) {
        this.setSpellbookFilter('all');
        this.spellbookSearch.value = '';
        this._applySpellbookFilter();
        this.onSpellbookSelect?.(DEFAULT_BOOK_ABILITY);
        this.closeSpellbook();
        return;
      }
      const choice = event.target.closest('[data-spellbook-element]');
      if (!choice) return;
      this.onSpellbookSelect?.(choice.dataset.spellbookElement);
      this.closeSpellbook();
    });

    this.stats = {
      fps: root.querySelector('[data-stat="fps"]'),
      particles: root.querySelector('[data-stat="particles"]'),
      spikes: root.querySelector('[data-stat="spikes"]'),
      calls: root.querySelector('[data-stat="calls"]')
    };
    this.help = root.querySelector('.hud__help');
    this.toast = root.querySelector('[data-toast]');
    this.pausedBadge = root.querySelector('[data-paused]');
    this.abilityBar = root.querySelector('.hud__abilities');
    this.setQuickAbility(DEFAULT_BOOK_ABILITY);
    this._applySpellbookFilter();
  }

  /** @param {{silent?: boolean}} [options] */
  setElement(element, options = {}) {
    for (const card of this.root.querySelectorAll('.ability-card')) {
      card.classList.toggle('is-active', card.dataset.element === element);
    }
    const meta = ELEMENT_META[element];
    if (meta && !options.silent) this.showToast(`${meta.hint} selected`);
  }

  /** Replace the dynamic T slot without rebuilding the whole ability bar. */
  setQuickAbility(element) {
    const meta = ELEMENT_META[element];
    if (!meta || !this.quickCard) return;
    const previousCards = (this.cards.get(this.quickAbility) || []).filter((card) => card !== this.quickCard);
    if (previousCards.length) this.cards.set(this.quickAbility, previousCards);
    else this.cards.delete(this.quickAbility);
    this.quickAbility = element;
    this.quickCard.dataset.element = element;
    this.quickCard.style.setProperty('--accent', meta.accent);
    this.quickCard.setAttribute('aria-label', `${meta.label}, shortcut T`);
    this.quickCard.querySelector('.ability-card__glyph').innerHTML = ELEMENT_SIGILS[element] ?? '';
    this.quickCard.querySelector('.ability-card__label').textContent = meta.label;
    this.quickCard.style.setProperty('--cooldown', 0);
    this.quickCard.style.setProperty('--reactivation', 0);
    this.quickCard.classList.remove('is-cooling', 'is-active', 'is-reactivatable');
    this.quickCard.querySelector('.ability-card__state').textContent = '';
    this._cooldownShown.delete(element);
    const elementCards = this.cards.get(element) || [];
    if (!elementCards.includes(this.quickCard)) elementCards.push(this.quickCard);
    this.cards.set(element, elementCards);
    for (const choice of this.root.querySelectorAll('[data-spellbook-element]')) {
      const selected = choice.dataset.spellbookElement === element;
      choice.classList.toggle('is-equipped', selected);
      choice.setAttribute('aria-pressed', String(selected));
    }
  }

  setSpellbookFilter(category = 'all') {
    this._spellbookFilter = category;
    for (const filter of this.root.querySelectorAll('[data-spellbook-filter]')) {
      const active = filter.dataset.spellbookFilter === category;
      filter.classList.toggle('is-active', active);
      filter.setAttribute('aria-pressed', String(active));
    }
    this._applySpellbookFilter();
  }

  _applySpellbookFilter() {
    if (!this.spellbook) return;
    const query = (this.spellbookSearch?.value || '').trim().toLowerCase();
    const category = this._spellbookFilter || 'all';
    let visibleCount = 0;

    for (const card of this.root.querySelectorAll('[data-spellbook-element]')) {
      const matchesCategory = category === 'all' || card.dataset.spellbookCategory === category;
      const matchesQuery = !query || card.dataset.spellbookSearch.includes(query);
      const visible = matchesCategory && matchesQuery;
      card.hidden = !visible;
      if (visible) visibleCount++;
    }

    for (const group of this.root.querySelectorAll('[data-spellbook-group]')) {
      group.hidden = ![...group.querySelectorAll('[data-spellbook-element]')].some((card) => !card.hidden);
    }
    if (this.spellbookCount) this.spellbookCount.textContent = String(visibleCount);
    if (this.spellbookEmpty) this.spellbookEmpty.hidden = visibleCount > 0;
  }

  openSpellbook() {
    if (!this.spellbook?.hidden) return;
    this._spellbookReturnFocus = document.activeElement;
    this.spellbook.hidden = false;
    this.spellbook.classList.add('is-open');
    this.root.classList.add('is-spellbook-open');
    this.root.style.setProperty('z-index', '10000', 'important');
    this._applySpellbookFilter();
    this.spellbookSearch?.focus();
  }

  /** @returns {boolean} whether an open spellbook was closed */
  closeSpellbook() {
    if (!this.spellbook || this.spellbook.hidden) return false;
    this.spellbook.classList.remove('is-open');
    this.spellbook.hidden = true;
    this.root.classList.remove('is-spellbook-open');
    this.root.style.removeProperty('z-index');
    this._spellbookReturnFocus?.focus?.();
    this._spellbookReturnFocus = null;
    return true;
  }

  /** Highlight the slot while a cast is armed. */
  setArmed(armed) {
    if (armed === this._armedShown) return;
    this._armedShown = armed;
    this.abilityBar.classList.toggle('is-armed', armed);
  }

  /**
   * Drive one slot's cooldown sweep. Cooldowns are per ability, so this is
   * called once per element each frame.
   *
   * @param {string} element
   * @param {number} remaining seconds left
   * @param {number} total     the full cooldown, for the sweep angle
   */
  setCooldown(element, remaining, total) {
    const cards = this.cards.get(element);
    if (!cards?.length) return;

    const ratio = Math.max(0, Math.min(1, remaining / Math.max(total, 0.001)));
    // Only touch the DOM when the sweep visibly moves.
    if (Math.abs(ratio - (this._cooldownShown.get(element) ?? -1)) < 0.01) return;
    this._cooldownShown.set(element, ratio);
    for (const card of cards) {
      card.style.setProperty('--cooldown', ratio);
      card.classList.toggle('is-cooling', ratio > 0.001);
    }
  }

  /** Show the held power filling the active slot without adding layout work. */
  setCharge(element, ratio) {
    for (const card of this.root.querySelectorAll('.ability-card.is-charging')) {
      if (!element || card.dataset.element !== element) {
        card.classList.remove('is-charging');
        card.style.setProperty('--charge', 0);
      }
    }
    if (!element) return;
    const value = Math.max(0, Math.min(1, ratio));
    for (const card of this.cards.get(element) || []) {
      card.classList.add('is-charging');
      card.style.setProperty('--charge', value);
    }
  }

  /** Communicate that the dynamic slot now consumes its existing shadow anchor. */
  setReactivation(element, available, remainingRatio = 0) {
    if (!this.quickCard || this.quickAbility !== element) return;
    const enabled = Boolean(available);
    this.quickCard.classList.toggle('is-reactivatable', enabled);
    this.quickCard.style.setProperty('--reactivation', Math.max(0, Math.min(1, remainingRatio)));
    const state = this.quickCard.querySelector('[data-reactivation-state]');
    if (state) state.textContent = enabled ? 'Troca Sombria' : '';
    const meta = ELEMENT_META[element];
    if (meta) {
      this.quickCard.setAttribute(
        'aria-label',
        enabled ? `Troca Sombria, shortcut T, shadow available` : `${meta.label}, shortcut T`
      );
    }
  }

  setPaused(paused) {
    this.pausedBadge.classList.toggle('is-visible', paused);
  }

  toggleHelp() {
    this.help.classList.toggle('is-hidden');
  }

  showToast(message, duration = 1600) {
    this.toast.textContent = message;
    this.toast.classList.add('is-visible');
    clearTimeout(this._toastTimer);
    this._toastTimer = setTimeout(() => this.toast.classList.remove('is-visible'), duration);
  }

  /**
   * @param {number} dt
   * @param {() => {particles:number, spikes:number, calls:number}} collect
   *   Called only when the readout actually refreshes, so gathering the numbers
   *   (which means walking the particle pools) stays off the hot path.
   */
  update(dt, collect) {
    this._frames++;
    this._statsAccumulator += dt;
    if (this._statsAccumulator < 0.4) return;

    this._fps = Math.round(this._frames / this._statsAccumulator);
    this._frames = 0;
    this._statsAccumulator = 0;

    const info = collect();
    this.stats.fps.textContent = this._fps;
    this.stats.particles.textContent = info.particles;
    this.stats.spikes.textContent = info.spikes;
    this.stats.calls.textContent = info.calls;
  }
}

/** Boot screen helper. */
export class LoadingScreen {
  constructor() {
    this.element = document.getElementById('loader');
    this.fill = document.getElementById('loader-fill');
    this.status = document.getElementById('loader-status');
  }

  setProgress(ratio, message) {
    this.fill.style.width = `${Math.round(Math.min(1, Math.max(0, ratio)) * 100)}%`;
    if (message) this.status.textContent = message;
  }

  hide() {
    this.setProgress(1);
    setTimeout(() => this.element.classList.add('is-hidden'), 220);
  }

  fail(message) {
    this.status.textContent = message;
    this.status.style.color = '#ff7a6a';
  }
}
